<template>
  <div class="map-container">
    <div 
      ref="mapRef" 
      class="leaflet-map"
      :style="{ width: width, height: height }"
    ></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon from 'leaflet/dist/images/marker-icon.png'

// 修复 Leaflet 打包后图标路径问题
// 设置 Leaflet 默认图标路径
L.Marker.prototype.options.icon = L.icon({
  iconUrl: markerIcon,
  iconSize: [20, 30],
  iconAnchor: [10, 30], // 必须设为中心点
  popupAnchor: [0, -15]
})

// 组件属性
const props = defineProps({
  // 地图宽度，默认100%
  width: {
    type: String,
    default: '100%'
  },
  // 地图高度，默认400px
  height: {
    type: String,
    default: '400px'
  },
  // 初始中心点坐标
  center: {
    type: Array,
    default: () => [39.9042, 116.4074] // 默认为北京坐标
  },
  // 初始缩放级别
  zoom: {
    type: Number,
    default: 15
  },
  // 是否显示缩放控件
  zoomControl: {
    type: Boolean,
    default: true
  },
  // 是否可以拖动
  dragging: {
    type: Boolean,
    default: true
  },
  // 是否可以双击缩放
  doubleClickZoom: {
    type: Boolean,
    default: true
  },
  // 是否显示默认比例尺
  showScale: {
    type: Boolean,
    default: true
  },
  // 高德地图类型：vec(矢量)、img(影像)、ter(地形)
  mapType: {
    type: String,
    default: 'vec',
    validator: (value) => ['vec', 'img', 'ter'].includes(value)
  }
})

// 组件事件
const emit = defineEmits(['map-ready', 'zoom-change', 'center-change'])

// 地图实例引用
const mapRef = ref(null)
const mapInstance = ref(null)

// 初始化地图
const initMap = () => {
  if (!mapRef.value) return
  
  // 创建地图实例
  mapInstance.value = L.map(mapRef.value, {
    center: props.center,
    zoom: props.zoom,
    zoomControl: props.zoomControl,
    dragging: props.dragging,
    doubleClickZoom: props.doubleClickZoom,
    attributionControl: false // 隐藏默认版权信息
  })
  
  // 添加高德底图
  addAmapLayer()
  
  // 添加比例尺
  if (props.showScale) {
    L.control.scale({
      position: 'bottomright',
      metric: true,
      imperial: false
    }).addTo(mapInstance.value)
  }
  
  // 绑定事件
  bindMapEvents()
  
  // 触发地图就绪事件
  emit('map-ready', mapInstance.value)
}

// 添加高德底图图层
const addAmapLayer = () => {
  if (!mapInstance.value) return
  
  // 高德地图URL模板
  let urlTemplate = ''
  let attribution = ''
  
  switch (props.mapType) {
    case 'vec':
      // 高德矢量底图
      urlTemplate = 'http://webrd0{s}.is.autonavi.com/appmaptile?lang=zh_cn&size=1&scale=1&style=8&x={x}&y={y}&z={z}'
      attribution = '© 高德地图'
      break
    case 'img':
      // 高德影像底图
      urlTemplate = 'http://webst0{s}.is.autonavi.com/appmaptile?style=6&x={x}&y={y}&z={z}'
      attribution = '© 高德地图'
      break
    case 'ter':
      // 高德地形底图
      urlTemplate = 'http://webst0{s}.is.autonavi.com/appmaptile?style=7&x={x}&y={y}&z={z}'
      attribution = '© 高德地图'
      break
  }
  
  // 创建并添加高德图层
  const amapLayer = L.tileLayer(urlTemplate, {
    subdomains: ['1', '2', '3', '4'],
    attribution: attribution,
    minZoom: 3,
    maxZoom: 18
  })
  
  amapLayer.addTo(mapInstance.value)
}

// 绑定地图事件
const bindMapEvents = () => {
  if (!mapInstance.value) return
  
  // 缩放变化事件
  mapInstance.value.on('zoomend', () => {
    emit('zoom-change', mapInstance.value.getZoom())
  })
  
  // 中心变化事件
  mapInstance.value.on('moveend', () => {
    const center = mapInstance.value.getCenter()
    emit('center-change', [center.lat, center.lng])
  })
}

// 组件挂载时初始化地图
onMounted(() => {
  initMap()
})

// 组件卸载时销毁地图
onUnmounted(() => {
  if (mapInstance.value) {
    mapInstance.value.remove()
    mapInstance.value = null
  }
})

// 监听地图类型变化
watch(() => props.mapType, () => {
  if (mapInstance.value) {
    // 清除现有图层
    mapInstance.value.eachLayer(layer => {
      if (layer instanceof L.TileLayer) {
        mapInstance.value.removeLayer(layer)
      }
    })
    // 添加新图层
    addAmapLayer()
  }
})

// 监听中心点变化
// watch(() => props.center, (newCenter) => {
//   if (mapInstance.value && newCenter && newCenter.length === 2) {
//     mapInstance.value.setView(newCenter, props.zoom)
//   }
// }, { deep: true })

// // 监听缩放级别变化
// watch(() => props.zoom, (newZoom) => {
//   if (mapInstance.value) {
//     mapInstance.value.setZoom(newZoom)
//   }
// })

// 暴露方法给父组件
defineExpose({
  // 获取地图实例
  getMap: () => mapInstance.value,
  
  // 设置地图中心点
  // setCenter: (lat, lng) => {
  //   if (mapInstance.value) {
  //     mapInstance.value.setView([lat, lng], props.zoom)
  //   }
  // },
  
  // // 设置缩放级别
  // setZoom: (zoom) => {
  //   if (mapInstance.value) {
  //     mapInstance.value.setZoom(zoom)
  //   }
  // },
  setView: (lat, lng, zoom) => {
    if (mapInstance.value) {
      mapInstance.value.flyTo([lat, lng], zoom)
    }
  },
  
  // 添加标记点
  addMarker: (lat, lng, options = {}) => {
    if (mapInstance.value) {
      return L.marker([lat, lng], options).addTo(mapInstance.value)
    }
    return null
  },
  
  // 添加弹出信息
  addPopup: (lat, lng, content, options = {}) => {
    if (mapInstance.value) {
      return L.popup(options)
        .setLatLng([lat, lng])
        .setContent(content)
        .openOn(mapInstance.value)
    }
    return null
  },
  
  // 清除所有标记
  clearMarkers: () => {
    if (mapInstance.value) {
      mapInstance.value.eachLayer(layer => {
        if (layer instanceof L.Marker || layer instanceof L.Popup) {
          layer.remove()
        }
      })
    }
  }
})
</script>

<style scoped>
.map-container {
  position: relative;
  overflow: hidden;
}

.leaflet-map {
  z-index: 1;
}

/* 修复Leaflet图标路径问题 */
:deep(.leaflet-default-icon-path) {
  background-image: url('data:image/svg+xml;base64,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');
}
.leaflet-map{
  background: #fff;
}
</style>